import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import { api, apiError } from '../lib/api';
import { INTERESTS } from '../lib/site';

const inputCls =
    'w-full bg-ink2 border border-line px-5 py-4 text-sm text-cream placeholder:text-fog/50 focus:outline-none focus:border-bronze transition-colors duration-300';

export default function ContactForm({ defaultInterest = '' }) {
    const [form, setForm] = useState({
        name: '',
        email: '',
        organization: '',
        interest: defaultInterest,
        message: '',
    });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post('/inquiries', form);
            toast.success('Inquiry received. A senior advisor will respond within one business day.');
            setForm({ name: '', email: '', organization: '', interest: defaultInterest, message: '' });
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                    data-testid="input-name"
                    required
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Full name"
                    className={inputCls}
                />
                <input
                    data-testid="input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    placeholder="Work email"
                    className={inputCls}
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                    data-testid="input-organization"
                    value={form.organization}
                    onChange={set('organization')}
                    placeholder="Organization"
                    className={inputCls}
                />
                <select
                    data-testid="input-interest"
                    required
                    value={form.interest}
                    onChange={set('interest')}
                    className={`${inputCls} ${form.interest ? '' : 'text-fog/50'} appearance-none`}
                >
                    <option value="" disabled>
                        Area of interest
                    </option>
                    {INTERESTS.map((i) => (
                        <option key={i} value={i}>
                            {i}
                        </option>
                    ))}
                </select>
            </div>
            <textarea
                data-testid="input-message"
                required
                rows={6}
                value={form.message}
                onChange={set('message')}
                placeholder="Tell us about your market adoption or corporate development objectives…"
                className={`${inputCls} resize-none`}
            />
            <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="group relative inline-flex items-center gap-3 bg-bronze text-ink font-mono text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full overflow-hidden disabled:opacity-60"
            >
                <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                <span className="relative">{sending ? 'Sending…' : 'Submit Inquiry'}</span>
                {sending ? (
                    <Loader2 size={14} className="relative animate-spin" />
                ) : (
                    <ArrowRight size={14} className="relative group-hover:translate-x-1 transition-transform duration-300" />
                )}
            </button>
        </form>
    );
}
